import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ButtonUIComponent, IconUIComponent } from '@shared-ui-kit';
import { WebCriticalFreightCard } from '../web-critical-freight-card/web-critical-freight-card';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TranslatePipe } from '@ngx-translate/core';
import { PERCENT_MAX } from '@shared-util-types';

/** Scroll progress (0–1) at which each step of the timeline counts as done. */
const STEP_TWO_DONE_PROGRESS = 0.2;
const STEP_THREE_DONE_PROGRESS = 0.4;
/** The cards track scrolls until its end is this far into the viewport (`innerWidth / n`). */
const TRACK_END_VIEWPORT_DIVISOR = 1.25;

gsap.registerPlugin(ScrollTrigger);

@Component({
  selector: 'fr8bot-web-critical-freight',
  templateUrl: './web-critical-freight.html',
  styleUrl: './web-critical-freight.scss',
  imports: [ButtonUIComponent, IconUIComponent, WebCriticalFreightCard, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebCriticalFreight implements OnDestroy {
  shipNow = output();

  private readonly sectionRef = viewChild.required<ElementRef<HTMLElement>>('sectionRef');
  private readonly cardsTrackRef = viewChild.required<ElementRef<HTMLElement>>('cardsTrackRef');

  scrollProgress = signal(0);

  progressWidth = computed(() => `${this.scrollProgress() * PERCENT_MAX}%`);
  vanLeft = computed(() => `${this.scrollProgress() * PERCENT_MAX}%`);
  isStepTwoDone = computed(() => this.scrollProgress() >= STEP_TWO_DONE_PROGRESS);
  isStepThreeDone = computed(() => this.scrollProgress() >= STEP_THREE_DONE_PROGRESS);

  private ctx?: gsap.Context;

  constructor() {
    afterNextRender(() => this.initScrollAnimation());
  }

  private initScrollAnimation(): void {
    this.ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        '(min-width: 1000px)': () => {
          const section = this.sectionRef().nativeElement;
          const track = this.cardsTrackRef().nativeElement;

          const scrollDistance = track.scrollWidth - window.innerWidth / TRACK_END_VIEWPORT_DIVISOR;

          gsap.to(track, {
            x: -scrollDistance,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              pin: true,
              scrub: 1,
              end: '+=2000',
              onUpdate: (self) => {
                this.scrollProgress.set(self.progress);
              },
            },
          });
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.ctx?.revert();
  }
}
